import { useState, useEffect } from 'react'
import {
  FormControl,
  ToggleButtonGroup,
  ToggleButton,
  FormLabel,
  FormHelperText,
} from '@mui/material'
import { noop } from 'lodash'
import './ButtonGroupInput.scss'
import { useI18n } from '@ggs/i18n'

/**
 * @typedef {object} ButtonOption
 * @property {string} label
 * @property {string} value
 */

/**
 * @typedef {Object} ButtonGroupFieldProps
 * @property {string} id
 * @property {boolean=} fullWidth
 * @property {boolean=} error
 * @property {Object=} formControlFields
 * @property {Object<string, string>=} formControlClasses
 * @property {string=} label
 * @property {Object<string, string>=} labelClasses
 * @property {string=} value
 * @property {boolean=} exclusive
 * @property {("primary"|"secondary"|"error"|"info"|"success"|"warning"|"standard")=} color
 * @property {boolean=} allowEmpty
 * @property {Object<string, string>=} buttonGroupClasses
 * @property {Object=} buttonGroupFields
 * @property {ButtonOption[]=} buttonOptions
 * @property {Object<string, string>=} buttonOptionClasses
 * @property {string=} helperText
 * @property {string=} errorHelperText
 * @property {Object=} helperTextFields
 * @property {Object<string, string>=} helperTextClasses
 * @property {function=} changeCallback
 */

/**
 * @param {ButtonGroupFieldProps} props
 * @return {JSX.Element}
 */
const ButtonGroupInput = ({
  id,
  fullWidth = true,
  error = false,
  formControlFields = {},
  changeCallback = noop,
  formControlClasses = {
    root: '',
  },
  label = '',
  labelClasses = {
    root: '',
    focused: '',
  },
  value,
  exclusive = true,
  color = 'primary',
  allowEmpty = false,
  buttonGroupClasses = {
    root: '',
    grouped: '',
  },
  buttonGroupFields = {},
  buttonOptions = [],
  buttonOptionClasses = {
    root: '',
    selected: '',
  },
  helperText = '',
  errorHelperText = '',
  helperTextFields = {},
  helperTextClasses = {
    root: '',
  },
}) => {
  const [btnValue, setBtnValue] = useState(value ?? buttonOptions[0].value ?? '')
  const { t } = useI18n()

  // @ts-ignore
  const handleChange = (_, value) => {
    if (allowEmpty || value !== null) {
      setBtnValue(value)
      changeCallback(value)
    }
  }

  // Affect local state if default value changed.
  useEffect(() => {
    setBtnValue(value)
  }, [value])

  return (
    <FormControl
      {...formControlFields}
      fullWidth={fullWidth}
      error={error}
      classes={{
        ...formControlClasses,
        root: `btn-group-field ${formControlClasses.root ?? ''}`.trim(),
      }}
    >
      {label && (
        <FormLabel
          htmlFor={id}
          classes={{
            ...labelClasses,
            root: `btn-group-field__label ${labelClasses.root ?? ''}`.trim(),
            focused: `btn-group-field__focused ${labelClasses.focused ?? ''}`.trim(),
          }}
        >
          {label}
        </FormLabel>
      )}
      <ToggleButtonGroup
        id={id}
        size="large"
        exclusive={exclusive}
        // @ts-ignore
        color={error ? 'error' : color}
        value={btnValue || value}
        aria-describedby={`${id}-helper-text`}
        onChange={handleChange}
        classes={{
          root: `btn-group-field__root ${buttonGroupClasses.root ?? ''}`.trim(),
          grouped: `btn-group-field__button ${buttonGroupClasses.grouped ?? ''}`.trim(),
        }}
        {...buttonGroupFields}
      >
        {buttonOptions.map((option, i) => (
          <ToggleButton
            key={i}
            value={option.value}
            classes={{
              ...buttonOptionClasses,
              root: `btn-group-field__button ${buttonOptionClasses.root ?? ''}`.trim(),
              selected: `btn-group-field__button--selected ${
                buttonOptionClasses.selected ?? ''
              }`.trim(),
            }}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <FormHelperText
        {...helperTextFields}
        id={`${id}-helper-text`}
        classes={{
          root: `btn-group-field__helper-text ${helperTextClasses.root ?? ''}`.trim(),
        }}
      >
        {error ? errorHelperText ?? t('Error occurred') : helperText}
      </FormHelperText>
    </FormControl>
  )
}
export default ButtonGroupInput
